import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { layersMenuStyles, paper } from '@constants/mui-theme';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Switch from '@material-ui/core/Switch';
import { withStyles } from '@material-ui/core/styles';
import { getToolbarConfig, getCurrentTraffic, getCurrentBicycle, getCurrentTransit } from '@selectors/map-selector';
import { togglePois, setMapTypeId, toggleTraffic, toggleBicycle, toggleTransit } from '@actions/map-actions';
import PoiMarkerIcon from '@icons/poi-marker-icon';
import { textColor } from '@constants/colors';
import DefaultMapTypeIcon from '@icons/data-uri-icon/default-map-type-icon';
import SatelliteMapTypeIcon from '@icons/data-uri-icon/satellite-map-type-icon';
import TerrainMapTypeIcon from '@icons/data-uri-icon/terrain-map-type-icon';
import { FormControlLabel, Icon } from '@mui';
import './layers-menu.scss';

const CompactFormControlLabel = withStyles({
  root: {
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
    justifyContent: 'space-between'
  }
})(FormControlLabel);

const RadioFormControlLabel = withStyles({
  root: {
    margin: 0,
    width: '100%',
    justifyContent: 'space-between'
  }
})(FormControlLabel);

const CompactRadio = withStyles({
  root: {
    padding: 0
  }
})(Radio);

const CompactSwitch = withStyles({
  root: {
    padding: 4,
    width: 40,
    height: 24
  },
  switchBase: {
    padding: 4,
    '&$checked': {
      transform: 'translateX(16px)'
    }
  },
  thumb: {
    width: 16,
    height: 16
  },
  track: {
    borderRadius: 10
  },
  checked: {}
})(Switch);

const LayersMenu = ({ small, style }) => {
  const dispatch = useDispatch();

  const traffic = useSelector(getCurrentTraffic);
  const transit = useSelector(getCurrentTransit);
  const bicycle = useSelector(getCurrentBicycle);
  const layersMenuOpen = useSelector(state => state.map.layersMenuOpen);
  const mapTypeId = useSelector(state => state.map.mapTypeId);
  const poisOn = useSelector(state => state.map.markers.poisOn);
  const toolsConfig = useSelector(getToolbarConfig);

  // Applying the poisOn style is handled by DotMapsGoogleMap, which reacts to the same
  // state.map.markers.poisOn value by swapping the map's mapId (see DOT-4736 memory notes) -
  // toggling here just needs to update the store.
  const setMapType = useCallback(event => {
    dispatch(setMapTypeId(event.target.value));
  }, [dispatch]);

  const handleTogglePois = useCallback(() => {
    dispatch(togglePois());
  }, [dispatch]);

  const handleToggleTraffic = useCallback(() => {
    dispatch(toggleTraffic());
  }, [dispatch]);

  const handleToggleTransit = useCallback(() => {
    dispatch(toggleTransit());
  }, [dispatch]);

  const handleToggleBicycle = useCallback(() => {
    dispatch(toggleBicycle());
  }, [dispatch]);

  if (!layersMenuOpen) {
    return null;
  }

  const {
    traffic: trafficConfig = true,
    transit: transitConfig = true,
    bicycle: bicycleConfig = true
  } = toolsConfig;

  return (
    <div styleName={`container ${small ? 'small' : ''}`} style={{ ...style, ...paper }}>
      <div styleName="header">MAP TYPE</div>
      <div>
        <RadioGroup
          name="layer-controls"
          value={mapTypeId}
          onChange={setMapType}
          style={layersMenuStyles.radioGroup.style}
        >
          <RadioFormControlLabel
            value="roadmap"
            control={<CompactRadio color="primary" size="small" />}
            label={
              <div styleName="label"><DefaultMapTypeIcon style={layersMenuStyles.menuIcons} />Default</div>
            }
            labelPlacement="start"
            style={layersMenuStyles.radio.style}
          />
          <RadioFormControlLabel
            value="hybrid"
            control={<CompactRadio color="primary" size="small" />}
            label={
              <div styleName="label"><SatelliteMapTypeIcon style={layersMenuStyles.menuIcons} />Satellite</div>
            }
            labelPlacement="start"
            style={layersMenuStyles.radio.style}
          />
          <RadioFormControlLabel
            value="terrain"
            control={<CompactRadio color="primary" size="small" />}
            label={
              <div styleName="label"><TerrainMapTypeIcon style={layersMenuStyles.menuIcons} />Terrain</div>
            }
            labelPlacement="start"
            style={layersMenuStyles.radio.style}
          />
        </RadioGroup>
      </div>
      <div styleName="header">MAP DETAILS</div>
      <div>
        <div styleName="input">
          <CompactFormControlLabel
            labelPlacement="start"
            control={
              <CompactSwitch
                checked={poisOn}
                onChange={handleTogglePois}
                color="primary"
              />
            }
            label={<div styleName="label"><PoiMarkerIcon color={textColor} />Places</div>}
          />
        </div>
        {trafficConfig &&
          <div styleName="input">
            <CompactFormControlLabel
              labelPlacement="start"
              control={
                <CompactSwitch
                  checked={traffic}
                  onChange={handleToggleTraffic}
                  color="primary"
                />
              }
              label={<div styleName="label"><Icon role="img">traffic</Icon>Traffic</div>}
            />
          </div>
        }
        {transitConfig &&
          <div styleName="input">
            <CompactFormControlLabel
              labelPlacement="start"
              control={
                <CompactSwitch
                  checked={transit}
                  onChange={handleToggleTransit}
                  color="primary"
                />
              }
              label={<div styleName="label"><Icon role="img">directions_transit</Icon>Transit</div>}
            />
          </div>
        }
        {bicycleConfig &&
          <div styleName="input">
            <CompactFormControlLabel
              labelPlacement="start"
              control={
                <CompactSwitch
                  checked={bicycle}
                  onChange={handleToggleBicycle}
                  color="primary"
                />
              }
              label={<div styleName="label"><Icon role="img">directions_bike</Icon>Biking</div>}
            />
          </div>
        }
      </div>
    </div>
  );
};

LayersMenu.propTypes = {
  small: PropTypes.bool,
  style: PropTypes.object
};

export default LayersMenu;
